/**
 * A short label for one dataset item or result in a picker: `#4 · SYN-005 ·
 * CT chest without…`. The preview is an id-like field when there is one, then
 * the first short text value — never the raw JSON.
 */

import { asJson, isBranch, oneLine } from "./json";

const ID_KEY = /(^|[-_ .])(id|key|ref)$/i;

function flat(value: Record<string, unknown>): [string, unknown][] {
  const { variables, ...rest } = value;
  return isBranch(variables) && !Array.isArray(variables)
    ? [...Object.entries(variables), ...Object.entries(rest)]
    : Object.entries(value);
}

const scalar = (v: unknown): v is string | number =>
  (typeof v === "string" && v.trim() !== "") || typeof v === "number";

/** The one-line preview of an item's input (or an output), without the number. */
export function itemPreview(value: unknown, max = 56): string {
  const parsed = asJson(value);
  if (!isBranch(parsed)) return oneLine(parsed ?? "", max);
  const entries = Array.isArray(parsed) ? parsed.map((v, i) => [String(i), v] as const) : flat(parsed);
  const id = entries.find(([k, v]) => ID_KEY.test(k) && scalar(v) && String(v).length <= 40)?.[1];
  const text = entries.find(([, v]) => typeof v === "string" && v.trim() && v !== id)?.[1];
  const parts = [id, text].filter((p) => p !== undefined).map(String);
  return parts.length ? oneLine(parts.join(" · "), max) : oneLine(parsed, max);
}

/** `#<n> · <preview>`, `n` counted from 1 like the item panels. */
export function itemLabel(position: number, value: unknown, max?: number): string {
  const preview = itemPreview(value, max);
  return preview ? `#${position + 1} · ${preview}` : `#${position + 1}`;
}
